import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { useSettings } from "../SettingsProvider";

type Theme = "light" | "dark" | "system";
type ResolvedTheme = "light" | "dark";

interface ThemeContextType {
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: Theme) => void;
  systemTheme: ResolvedTheme;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  const { settings, updateSettings } = useSettings();
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>("light");
  const [currentTheme, setCurrentTheme] = useState<Theme>("system");

  // Synchroniser le thème avec les settings
  useEffect(() => {
    if (settings?.theme) {
      setCurrentTheme(settings.theme as Theme);
    } else {
      // Fallback vers localStorage si les settings ne sont pas disponibles
      if (typeof window !== "undefined") {
        const savedTheme = localStorage.getItem("theme");
        if (savedTheme && ["light", "dark", "system"].includes(savedTheme)) {
          setCurrentTheme(savedTheme as Theme);
        }
      }
    }
  }, [settings?.theme]);

  const theme = currentTheme;

  // Calculer le thème résolu
  const resolvedTheme: ResolvedTheme =
    theme === "system" ? systemTheme : (theme as ResolvedTheme);

  // Fonction pour détecter le thème système
  const getSystemTheme = (): ResolvedTheme => {
    if (typeof window !== "undefined") {
      return window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light";
    }
    return "light";
  };

  // Fonction pour mettre à jour le thème
  const setTheme = async (newTheme: Theme) => {
    // Sauvegarder immédiatement dans localStorage comme fallback
    if (typeof window !== "undefined") {
      localStorage.setItem("theme", newTheme);
    }

    // Mettre à jour les settings si disponibles
    if (settings && updateSettings) {
      try {
        await updateSettings({ theme: newTheme });
      } catch (error) {
        console.error("Erreur lors de la mise à jour du thème:", error);
      }
    }
  };

  // Écouter les changements du thème système
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = (e: MediaQueryListEvent) => {
      setSystemTheme(e.matches ? "dark" : "light");
    };

    // Initialiser le thème système
    setSystemTheme(getSystemTheme());

    // Écouter les changements
    mediaQuery.addEventListener("change", handleChange);

    return () => {
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, []);

  // Appliquer le thème au DOM
  useEffect(() => {
    const root = window.document.documentElement;

    // Supprimer les classes de thème existantes
    root.classList.remove("light", "dark");

    // Ajouter la nouvelle classe de thème
    root.classList.add(resolvedTheme);

    // Optionnel: mettre à jour l'attribut data-theme pour d'éventuels styles CSS personnalisés
    root.setAttribute("data-theme", resolvedTheme);
  }, [resolvedTheme]);

  const value: ThemeContextType = {
    theme,
    resolvedTheme,
    setTheme,
    systemTheme,
  };

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

// Exporter les hooks utilitaires
export { useThemeClasses, useThemeColors } from "./useThemeClasses";

// Export des utilitaires
export * from "./useThemeClasses";
